/**
 * Telas de cliente/conversa migradas do shell de fixture para DADOS REAIS
 * (Task 10, finding "migração parcial das telas de fixture").
 *
 * As três telas que o shell `src/server.ts` renderizava de fixture
 * (`renderCustomer360`, `renderConversationDetail`, `renderLeadScoreDetail`)
 * passam a ser montadas aqui, a partir dos registros que a API devolve, e
 * continuam satisfazendo os MESMOS contratos de `packages/ui`
 * (`Customer360`, `ConversationDetail`, `LeadScoreDetail`) — validados pelos
 * `assert*` de lá. A fixture segue no repo só como cenário de teste (D-10.6).
 *
 * Invariantes deste módulo:
 *
 * 1. **Puro e sem I/O**: recebe dataset + eventos de auditoria e devolve
 *    estrutura; nada de fetch, env ou DOM (testável sem navegador).
 * 2. **Ausência de fonte não vira zero.** O histórico de lead score ainda não
 *    tem fonte publicada e aparece como ausente, nunca como "nunca mudou"
 *    (D-10.21).
 * 3. **Mesmos registros, mesmo histórico**: cada total devolve também a lista
 *    de registros que o compõe, para a tela navegar até eles. A visão 360 que
 *    aplicava isso aqui passou para `crm/customer360.ts` na Task 13 (ver a
 *    nota de continuação abaixo).
 */
import {
  assertConversationDetail,
  assertLeadScoreDetail,
  type AuditLogEntry,
  type ConversationDetail,
  type ConversationMessage as UiConversationMessage,
  type LeadScoreDetail,
  type LeadStage
} from "../../../../packages/ui/src/types.js";
import type { DashboardDataset } from "./kpis.js";
import type { PlatformAuditEvent, PlatformConversation, PlatformOrder } from "../api/types.js";

/** Só dígitos — comparação de telefone entre canais que formatam diferente. */
export function normalizePhone(value: string | null | undefined): string {
  if (!value) return "";
  const digits = value.replace(/\D+/g, "");
  // Compara pelo sufixo: o mesmo número aparece com e sem DDI (55) dependendo
  // da origem (WhatsApp manda com DDI, formulário de checkout nem sempre).
  return digits.length > 11 ? digits.slice(-11) : digits;
}

/**
 * Estágio de lead derivado do score. A fonte REAL do estágio é o pipeline de
 * CRM (`crm_opportunities`, migration 0032, Task 13); até ela existir o campo
 * obrigatório do contrato `Customer360` é preenchido por faixa de score e a
 * tela marca que é estimativa (D-10.20) — nunca apresentado como estágio
 * confirmado por um operador.
 */
export function leadStageFromScore(score: number): LeadStage {
  if (score >= 80) return "negociacao";
  if (score >= 60) return "proposta";
  if (score >= 30) return "qualificando";
  return "novo";
}

/** Rótulo do remetente na timeline. "IA" e "Operação" não se confundem. */
export function senderLabel(conversation: PlatformConversation, sender: "customer" | "ai" | "human"): string {
  if (sender === "customer") return conversation.displayName || conversation.customerId;
  if (sender === "ai") return "Agente de IA";
  return "Operação (humano)";
}

/**
 * Conversa real -> `ConversationDetail` de `packages/ui`. `null` quando a
 * conversa não tem mensagem (não existe timeline a mostrar; o contrato de UI
 * proíbe detalhe sem mensagem).
 */
/** Rótulo de mensagem sem texto (foto, áudio, figurinha…): o contrato de UI exige `text` não vazio. */
const MEDIA_ONLY_LABEL: Record<string, string> = { image: "📷 Foto", video: "🎥 Vídeo", audio: "🎤 Áudio", sticker: "Figurinha", document: "📄 Documento", location: "📍 Localização", contact: "Contato", poll: "Enquete" };

export function toConversationDetail(conversation: PlatformConversation): ConversationDetail | null {
  if (!conversation.messages.length) return null;
  const last = conversation.messages.at(-1);
  const messages: UiConversationMessage[] = conversation.messages.map((message) => ({
    id: message.id,
    sender: message.sender,
    senderLabel: senderLabel(conversation, message.sender),
    // Bug "tela de erro em qualquer conversa": mídia sem legenda chega com `text: ""` e
    // `assertConversationDetail` lançava durante a renderização, derrubando a tela inteira.
    text: message.text || MEDIA_ONLY_LABEL[(message as { messageType?: string }).messageType ?? ""] || "Mensagem",
    sentAt: message.createdAt
    // `aiDraft` (AI Response Preview) NÃO é inventado: o runtime de conversa
    // publica apenas mensagens já enviadas. Quando a rota publicar rascunho
    // pendente, ele entra aqui com o mesmo contrato.
  }));
  const detail: ConversationDetail = {
    id: conversation.id,
    customerId: conversation.customerId,
    customerName: conversation.displayName || conversation.customerId,
    channel: conversation.channel === "whatsapp" ? "whatsapp" : "webchat",
    status: conversation.mode === "human" && last?.sender === "customer" ? "pending" : "open",
    messages
  };
  assertConversationDetail(detail);
  return detail;
}

/** Fontes de conhecimento citadas pela IA na conversa (quando existirem). */
export function citedSources(conversation: PlatformConversation): Array<{ documentId: string; title: string }> {
  const seen = new Map<string, string>();
  for (const message of conversation.messages) {
    for (const hit of message.sources ?? []) {
      if (!seen.has(hit.documentId)) seen.set(hit.documentId, hit.title);
    }
  }
  return [...seen].map(([documentId, title]) => ({ documentId, title }));
}

/** Pedidos vinculados ao cliente pelo telefone da conversa (identidade real). */
export function ordersOfCustomer(dataset: DashboardDataset, phones: string[]): PlatformOrder[] {
  const wanted = new Set(phones.map(normalizePhone).filter(Boolean));
  if (!wanted.size) return [];
  return dataset.orders.filter((order) => wanted.has(normalizePhone(order.customer?.phone)));
}

/**
 * Evento do event store -> `AuditLogEntry` de `packages/ui`. O ator
 * `customer` do envelope não existe no contrato de UI: entra como `human`
 * com rótulo "Cliente", para não se passar por ação da operação nem do sistema.
 */
export function toAuditLogEntry(event: PlatformAuditEvent): AuditLogEntry {
  const actorType: AuditLogEntry["actorType"] =
    event.actorType === "ai" ? "ai" : event.actorType === "system" ? "system" : "human";
  const actorLabel =
    event.actorType === "customer"
      ? "Cliente"
      : event.actorType === "ai"
        ? "Agente de IA"
        : event.actorType === "system"
          ? "Sistema"
          : (event.actorId ?? "Operação");
  return {
    id: event.id,
    occurredAt: event.occurredAt,
    actorType,
    actorLabel,
    action: event.eventType,
    details: `${event.aggregateType} ${event.aggregateId}`
  };
}

/**
 * NOTA DE CONTINUAÇÃO (Task 13): a visão 360 em si **saiu deste arquivo**.
 *
 * Até a Task 12 ela era montada aqui a partir de conversas + pedidos do
 * checkout vinculados por telefone, com três lacunas declaradas no próprio
 * código: estágio de lead estimado por faixa de score, `tags` sempre vazias e
 * pedidos vinculáveis só por telefone. A Task 13 fecha as três agregando o
 * cliente CANÔNICO (0020), os pedidos unificados multicanal (0031), as
 * oportunidades/orçamentos/tarefas (0032) e os segmentos — então existe **uma**
 * visão 360, em `apps/plataforma/src/crm/customer360.ts` (`buildCrm360`).
 *
 * Os helpers abaixo continuam aqui porque são compartilhados: `buildCrm360`,
 * a tela de conversa e a de lead score os usam. Duas funções montando o
 * contrato `Customer360` dariam dois 360 com números diferentes — exatamente o
 * que a regra "mesmos registros, mesmo histórico" proíbe.
 */

export interface LeadScoreView {
  /** Contrato de `packages/ui`, já validado por `assertLeadScoreDetail`. */
  detail: LeadScoreDetail;
  /**
   * `false` = nenhuma fonte publica o histórico do score. O tipo de evento
   * `LEAD_SCORE_CHANGED` existe em `packages/event-contracts`, mas nenhum
   * runtime o emite hoje e `/api/admin/audit` não expõe payload — então o
   * histórico aparece como fonte ausente, nunca como "nunca mudou" (D-10.21).
   */
  historyAvailable: boolean;
  conversations: PlatformConversation[];
}

export function buildLeadScoreDetail(dataset: DashboardDataset, customerId: string): LeadScoreView | null {
  const conversations = dataset.conversations
    .filter((conversation) => conversation.customerId === customerId)
    .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt));
  if (!conversations.length) return null;
  const latest = conversations[conversations.length - 1];
  const detail: LeadScoreDetail = {
    customerId,
    customerName: latest.displayName || customerId,
    currentScore: latest.leadScore,
    history: []
  };
  assertLeadScoreDetail(detail);
  return { detail, historyAvailable: false, conversations };
}
